<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { BaseIcon } from '@magma-app/magma-lapilli';
import type { HelperRelationState } from '@/composables/useRelationState';

const { locale } = useI18n();
const props = defineProps<{
  state: HelperRelationState;
  prospectFirstname: string;
  points: number;
  rewardPoints: number;
  metaStatus?: string | null;
  reviewedBy?: 'admin' | 'system' | null;
  hasPositiveFeedback?: boolean;
}>();
const successful = computed(() =>
  ['accepted', 'exchangeValidated'].includes(props.state)
);
const icon = computed(() =>
  successful.value
    ? 'check-circle'
    : props.state === 'reported'
      ? 'flag'
      : 'info'
);
const titleKey = computed(() =>
  props.state === 'accepted' &&
  locale.value === 'fr' &&
  /^[aeiouyhàâéèêëîïôùûüœ]/i.test(props.prospectFirstname)
    ? 'helperRelation.state.accepted.titleVowel'
    : props.state === 'closedUnreviewed' && props.reviewedBy === 'system'
      ? 'helperRelation.state.closedUnreviewed.automatic'
      : `helperRelation.state.${props.state}.title`
);
</script>

<template>
  <section
    class="flex flex-wrap items-center gap-4 rounded-lg p-4"
    :class="
      successful
        ? 'bg-background-success-default'
        : [
              'toContact',
              'toConfirm',
              'waitingHelpee',
              'waitingTeam',
              'contacted'
            ].includes(state)
          ? 'bg-background-brand-default'
          : 'bg-background-subtle'
    "
    data-cy="relation-state-panel"
  >
    <div class="flex min-w-0 flex-[1_1_220px] gap-3">
      <BaseIcon
        :icon="icon"
        class="mt-0.5 shrink-0"
        :color="
          successful
            ? 'var(--color-foreground-success-default)'
            : 'var(--color-foreground-brand-default)'
        "
      />
      <div class="min-w-0 text-mgm-txt-sm text-foreground-default">
        <p class="font-medium text-foreground-emphasis">
          {{ $t(titleKey, { name: prospectFirstname }) }}
        </p>
        <p
          v-if="!successful && state !== 'reported'"
          class="mt-1 text-foreground-default"
        >
          {{
            $t(`helperRelation.state.${state}.body`, {
              name: prospectFirstname
            })
          }}
        </p>
        <template v-if="successful">
          <p class="mt-1" data-cy="relation-points-line">
            {{
              $t('helperRelation.state.exchangeValidated.points', { points })
            }}
          </p>
          <p v-if="state === 'accepted' && rewardPoints > 0" class="mt-1">
            {{
              $t('helperRelation.state.accepted.additionalPoints', {
                points: rewardPoints
              })
            }}
          </p>
          <p
            v-if="
              state === 'exchangeValidated' && !metaStatus && rewardPoints > 0
            "
            class="mt-2 text-foreground-subtle"
            data-cy="relation-pending-reward"
          >
            {{
              $t('helperRelation.state.exchangeValidated.pendingReward', {
                points: rewardPoints
              })
            }}
          </p>
        </template>
        <p
          v-if="state === 'toConfirm' && hasPositiveFeedback"
          class="mt-2 flex items-center gap-2 text-mgm-txt-xs text-foreground-subtle"
          data-cy="relation-feedback-teaser"
        >
          <BaseIcon icon="eye" :size="16" />{{
            $t('helperRelation.state.toConfirm.feedbackTeaser')
          }}
        </p>
      </div>
    </div>
    <slot />
  </section>
</template>
